import React, { useState, useEffect, useRef, useCallback } from "react";
import Grid from "@mui/material/Grid2";

import { 
  Campaign,
  CampaignNote,
  CampaignObject,
  Encounter, 
} from "@dndModels";

import AddButton from "@components/input/buttons/AddButton";
import CampaignNoteInput from "./CampaignNoteInput";
import DandDAPI from "@dndUtilities/system_api";
import { CampaignObjectType, DataType, isCampaignObjectType } from "@dndModels/Types";
import LoadingBox from "@components/input/LoadingBox";
import CommonUtilities from "@utilities/common_utilities";
import FlexBox from "@components/layout/FlexBox";
import { TYPE_NAME_MAP } from "@dndModels/Constants";

interface Props {
  campaign: Campaign;
  on_type?: DataType;
  on_id?: string;
}



const api = DandDAPI.getInstance();
const common_util = CommonUtilities.getInstance();

export default function CampaignNotesInput({
  campaign,
  on_type,
  on_id
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [ping, setPing] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const mounted = useRef(false);

  useEffect(() => { 
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  const load = useCallback( async () => {
    if (campaign.notes.length === 0) {
      campaign.notes = await api.getCampaignObjects<CampaignNote>({ data_type: "campaign_note", campaign });
      const on_types = common_util.unique_array(campaign.notes.map(o => o.on_type)).filter(o => o !== "campaign") as CampaignObjectType[];
      for (const type of on_types) {
        // Make sure we have the objects of that type.
        // Check each of the types that we do notes for.
        switch (type) {
          case "campaign_char": 
            if (campaign.characters.length === 0) {
              // For chars we want to get the players and the chars
              await api.loadCPlayers({ campaign, include_players: true });
              await api.loadCChars({ campaign, include_players: true, include_chars: true });
            }
          break;
          case "campaign_deck":
            if (campaign.c_decks.length === 0) {
              debugger; // I haven't gotten decks working again after the massive campaign changes.
            }
          break;
          case "campaign_npc":
            if (campaign.npcs.length === 0) {
              debugger; // These aren't implemented yet.
            }
          break;
          case "encounter":
            if (campaign.encounters.length === 0) {
              campaign.encounters = await api.getCampaignObjects<Encounter>({ data_type: "encounter", campaign });
            }
          break;
        }
      }
    }
    setLoading(false);
  }, [campaign]);

  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      setLoading(true);
      setTimeout(() => {
        load();
      }, 100);
    }
  }, [fetched, load]);
    
  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  const renderNotes = () => {
    if (on_type && on_id) {
      const notes = campaign.notes.filter(o => o.on_type === on_type && o.on_id === on_id);
      return renderNotesOn(notes);
    } else {
      const on_types = common_util.unique_array(campaign.notes.map(o => o.on_type));
      return on_types.map(data_type => {
        const notes_on_type = campaign.notes.filter(o => o.on_type === data_type);
        if (isCampaignObjectType(data_type)) {
          return (
            <FlexBox xs_expand={1} key={data_type} direction="column">
              { renderNotesOnType(notes_on_type, data_type) }
            </FlexBox>
          );
        } else {
          return (
            <FlexBox xs_expand={1} key={data_type} direction="column">
              <FlexBox className="header padded">
                Campaign Notes
              </FlexBox>
              { renderNotesOn(notes_on_type) }
            </FlexBox>
          );
        }
      });
    }
  }

  const renderNotesOnType = (notes: CampaignNote[], data_type: CampaignObjectType) => {
    // Get the objects these are notes for, so they can be shown above the notes.
    const on_ids = notes.map(o => o.on_id);
    let c_objects: CampaignObject[] = [];
    switch (data_type) {
      case "campaign_char": 
        c_objects = campaign.c_chars.filter(o => on_ids.includes(o._id));
      break;
      case "campaign_deck":
        c_objects = campaign.c_decks.filter(o => on_ids.includes(o._id));
      break;
      case "campaign_npc":
        c_objects = campaign.npcs.filter(o => on_ids.includes(o._id));
      break;
      case "encounter":
        c_objects = campaign.encounters.filter(o => on_ids.includes(o._id));
      break;
    }
    const type_name = TYPE_NAME_MAP[data_type].singular;
    return c_objects.map(c_obj => {
      const notes_on_id = notes.filter(o => o.on_id === c_obj._id);
      return (
        <FlexBox xs_expand={1} key={c_obj._id} direction="column">
          <FlexBox className="header padded">
            { type_name }: { c_obj.name }
          </FlexBox>
          { renderNotesOn(notes_on_id) }
        </FlexBox>
      );
    });
  }

  const renderNotesOn = (notes: CampaignNote[]) => {
    return notes.map(note => {
      return (
        <FlexBox key={note._id}>
          <CampaignNoteInput
            campaign={campaign}
            note={note} 
            onDelete={() => {
              api.deleteObject("campaign_note", note);
              campaign.notes = campaign.notes.filter(o => o._id !== note._id);
              setPing(!ping);
            }} 
          />
        </FlexBox>
      );
    });
  }

  if (!fetched || loading) {
    return (<LoadingBox />);
  }
  return (
    <FlexBox direction="column">
      <FlexBox>
        <AddButton label="Add Note" 
          disabled={processing}
          onClick={ async () => {
            const new_note = new CampaignNote();
            new_note.campaign_id = campaign._id;
            if (on_type && on_id) {
              new_note.on_type = on_type;
              new_note.on_id = on_id;
            }
            setProcessing(true);
            await api.upsertObject("campaign_note", new_note);
            campaign.notes.push(new_note);
            setProcessing(false);
          }}
        />
      </FlexBox>
      { renderNotes() }
    </FlexBox>
  );
}
